import {
  clipMentionFacetsQueryKey,
  getClipMentionFacets,
} from '@/features/clips/api/get-clip-mention-facets';
import { SEGMENTS_REFRESH_INTERVAL_MS } from '@/features/segments/api/get-infinite-segments';
import { getSegmentMentionFacets } from '@/features/segments/api/get-segment-mention-facets';
import { segmentsQueryKeys } from '@/features/segments/api/query-keys';
import { appWallClockParts } from '@/utils/spain-time';

/**
 * Narrows a filter's options to the values present in the list's current date range. The
 * source names which list the range applies to, since each list is answered by different
 * requests.
 */
export type TRangeActiveOptions = {
  source: 'segments' | 'clips';
  gte: string;
  lte: string;
};

/** The ids present in a broadcast's segments, per dimension. */
export type TBroadcastActiveIds = {
  workspaces: string[];
  tags: string[];
  logos: string[];
  people: string[];
};

/**
 * Narrows a filter's options to ids already resolved by the caller, with their loading state.
 * `ids` stays undefined until they are known.
 */
export type TBroadcastActiveOptions = {
  source: 'programBroadcast';
  ids: TBroadcastActiveIds | undefined;
  isFetching: boolean;
  isError: boolean;
};

export type TActiveOptions = TRangeActiveOptions | TBroadcastActiveOptions;

/** The sources narrowed by a date range, the only ones the range machinery answers. */
export type TActiveOptionsSource = TRangeActiveOptions['source'];

export type TRange = { gte?: string; lte?: string };

export type TRangeParams =
  | { segmentStartDatetimeGte?: string; segmentEndDatetimeLte?: string }
  | { clipStartDatetimeGte?: string; clipEndDatetimeLte?: string };

export type TMentionFacets = {
  workspaces: string[];
  tags: string[];
};

type TActiveOptionsSourceConfig = {
  rangeParams: (range: TRange) => TRangeParams;
  staleTime: number;
  facets: {
    queryKey: (range: TRange) => readonly unknown[];
    fetch: (range: TRange) => Promise<TMentionFacets>;
    staleTime: (range: TRange) => number;
  };
};

const CLIPS_ACTIVE_OPTIONS_STALE_TIME = 30 * 1000;
const LONG_RANGE_DAYS = 31;

const appDayNumber = (instant: string) =>
  Date.parse(`${appWallClockParts(instant).date}T00:00:00.000Z`) / (24 * 60 * 60 * 1000);

// RISK: counted in Madrid calendar days, never in milliseconds nor in the browser's zone. The
// date control bounds Madrid days, a month crossing the autumn clock change lasts an hour longer
// than 31 days, and a viewer elsewhere would see the same range straddle one day more.
const isLongRange = ({ gte, lte }: TRange) => {
  if (!gte || !lte || Number.isNaN(Date.parse(gte)) || Number.isNaN(Date.parse(lte))) return true;
  return appDayNumber(lte) - appDayNumber(gte) + 1 > LONG_RANGE_DAYS;
};

export const ACTIVE_OPTIONS_SOURCES: Record<TActiveOptionsSource, TActiveOptionsSourceConfig> = {
  segments: {
    rangeParams: ({ gte, lte }) => ({
      segmentStartDatetimeGte: gte || undefined,
      segmentEndDatetimeLte: lte || undefined,
    }),
    staleTime: SEGMENTS_REFRESH_INTERVAL_MS,
    facets: {
      queryKey: segmentsQueryKeys.mentionFacets,
      fetch: getSegmentMentionFacets,
      staleTime: () => SEGMENTS_REFRESH_INTERVAL_MS,
    },
  },
  clips: {
    rangeParams: ({ gte, lte }) => ({
      clipStartDatetimeGte: gte || undefined,
      clipEndDatetimeLte: lte || undefined,
    }),
    staleTime: CLIPS_ACTIVE_OPTIONS_STALE_TIME,
    facets: {
      queryKey: clipMentionFacetsQueryKey,
      fetch: getClipMentionFacets,
      // RISK: the backend accepted the facets' cost - over a second on a year of clips - on
      // the premise of one request per range. A long range must never be refetched on open.
      staleTime: range => (isLongRange(range) ? Infinity : CLIPS_ACTIVE_OPTIONS_STALE_TIME),
    },
  },
};

export const rangeParams = ({ source, gte, lte }: { source: TActiveOptionsSource } & TRange) =>
  ACTIVE_OPTIONS_SOURCES[source].rangeParams({ gte, lte });

export const activeOptionsStaleTime = (source: TActiveOptions['source']) =>
  source === 'programBroadcast' ? Infinity : ACTIVE_OPTIONS_SOURCES[source].staleTime;

export type TOptionItem = {
  value: string;
  label: string;
  image?: string;
};

/**
 * The active options sorted by label, followed by every selected value they lack. A selected
 * value must stay visible and uncheckable even when it is not active in the range, so it is
 * named from `resolved` and, failing that, from `fallbackLabel`.
 */
export function withSelectedOptions(
  active: TOptionItem[],
  selected: string[],
  resolved: TOptionItem[],
  fallbackLabel: string
): TOptionItem[] {
  const sorted = [...active].sort((a, b) => a.label.localeCompare(b.label));
  const listed = new Set(sorted.map(item => item.value));
  const resolvedById = new Map(resolved.map(item => [item.value, item]));

  const missing = selected
    .filter(id => !listed.has(id))
    .map(id => resolvedById.get(id) ?? { value: id, label: fallbackLabel });

  return [...sorted, ...missing];
}

export const missingSelected = (active: { value: string }[], selected: string[]) => {
  const listed = new Set(active.map(item => item.value));
  return selected.filter(id => !listed.has(id));
};
